import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import classNames from "classnames";
import Select from "react-select";
import "./villageCalendarsCreateEditTemplate.sass";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  Label
} from "reactstrap";
import {useStores} from "../../../stores/useStores";
import {KeyValue} from "../../../stores/observables/keyValue";
import {RoutePaths} from "../../../shared/routePaths";
import {
  VillageCalendarConfig,
  VillageCalendarConfigPayload
} from "../../../shared/interfaces/villageCalendarConfig";

export interface VillageCalendarsCreateEditTemplateProps {
  existing?: VillageCalendarConfig;
  defaultVillageId?: string;
  defaultVillageName?: string;
  lockVillage?: boolean;
  onSave: (payload: VillageCalendarConfigPayload) => Promise<void>;
}

/// Returns the initial person type IDs, prioritizing CMS person type IDs over legacy person type.
/// If no CMS person type IDs are available, it falls back to the legacy person type.
const getInitialPersonTypeIds = (existing?: VillageCalendarConfig): string[] => {
  const cmsPersonTypeIds = existing?.personTypeIds?.filter((id) => !!id) ?? [];
  if (cmsPersonTypeIds.length > 0) return cmsPersonTypeIds;

  const legacyPersonType =
    existing?.personType == null ? "" : String(existing.personType);
  return legacyPersonType && legacyPersonType !== "0" ? [legacyPersonType] : [];
};

const VillageCalendarsCreateEditTemplate: React.FC<
  VillageCalendarsCreateEditTemplateProps
> = ({existing, defaultVillageId, defaultVillageName, lockVillage, onSave}) => {
  const {t} = useTranslation(["translation", "villageCalendars"]);
  const history = useHistory();
  const {
    domainStore: {personTypesDomainStore},
    uiStore: {villageCalendarsUiStore, accountStore}
  } = useStores();

  const availableVillages = React.useMemo(
    () =>
      (accountStore.villages ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [accountStore.villages]
  );
  const personTypeOptions = React.useMemo(
    () =>
      personTypesDomainStore.personTypes.map(
        (personType) => new KeyValue(personType.id, personType.name)
      ),
    [personTypesDomainStore.personTypes]
  );

  const [villageId, setVillageId] = useState<string | undefined>(
    existing?.villageId != null ? String(existing.villageId) : defaultVillageId
  );
  const [name, setName] = useState<string>(existing?.name ?? "");
  const [calendarType, setCalendarType] = useState<string>(
    existing?.calendarType ?? ""
  );
  const [calendarColor, setCalendarColor] = useState<string>(
    existing?.calendarColor ?? "#3f80ea"
  );
  const [personTypeIds, setPersonTypeIds] = useState<string[]>(() =>
    getInitialPersonTypeIds(existing)
  );
  const [hasCareCompanion, setHasCareCompanion] = useState<boolean>(
    !!existing?.hasCareCompanion
  );
  const selectedPersonTypeOptions = React.useMemo(
    () =>
      personTypeIds
        .map(
          (id) =>
            personTypeOptions.find((personType) => personType.value === id) ??
            new KeyValue(id, personTypesDomainStore.getById(id)?.name ?? id)
        )
        .filter(
          (option, index, all) =>
            all.findIndex((candidate) => candidate.value === option.value) ===
            index
        ),
    [personTypeIds, personTypeOptions, personTypesDomainStore]
  );

  useEffect(() => {
    if (!personTypesDomainStore.isLoaded) {
      personTypesDomainStore.loadPersonTypes().catch(() => {
        /* falls back to raw ids until the fetch succeeds */
      });
    }
  }, [personTypesDomainStore]);

  const villageDisplayName =
    existing?.villageName ??
    availableVillages.find((v) => String(v.id) === String(villageId))?.name ??
    defaultVillageName ??
    (villageId != null ? `Village ${villageId}` : "");

  const canSave =
    villageId != null &&
    name.trim().length > 0 &&
    calendarType.trim().length > 0;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave || villageId == null) return;
    try {
      villageCalendarsUiStore.setSaving(true);
      villageCalendarsUiStore.setErrorMessage("");
      const payload: VillageCalendarConfigPayload = {
        id: existing?.id,
        villageId,
        name: name.trim(),
        calendarType: calendarType.trim(),
        calendarColor,
        personTypeIds,
        ...(personTypeIds.length === 1 ? {personType: personTypeIds[0]} : {}), // Sets the legacy person type if exactly one CMS person type ID is selected
        hasCareCompanion
      };
      await onSave(payload);
      history.push(RoutePaths.VillageCalendars);
    } catch (err) {
      villageCalendarsUiStore.setErrorMessage(
        t("villageCalendars:messages.saveError")
      );
    } finally {
      villageCalendarsUiStore.setSaving(false);
    }
  };

  return (
    <div
      className="village-calendars-create-edit-template"
      data-testid="villageCalendarsCreateEditTemplate"
    >
      <Card>
        <CardHeader>
          <h4 className="mb-0">
            {existing
              ? t("villageCalendars:titles.edit")
              : t("villageCalendars:titles.create")}
          </h4>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label for="vc-village">
                {t("villageCalendars:labels.village")}
              </Label>
              {existing || lockVillage ? (
                <Input
                  id="vc-village"
                  data-testid="vc-village"
                  type="text"
                  value={villageDisplayName}
                  readOnly
                  plaintext
                />
              ) : (
                <Input
                  type="select"
                  id="vc-village"
                  data-testid="vc-village"
                  value={villageId != null ? String(villageId) : ""}
                  onChange={(e) =>
                    setVillageId(e.target.value ? e.target.value : undefined)
                  }
                >
                  <option value="">
                    {t("villageCalendars:placeholders.selectVillage")}
                  </option>
                  {availableVillages.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </Input>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="vc-name">{t("villageCalendars:labels.name")}</Label>
              <Input
                id="vc-name"
                data-testid="vc-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label for="vc-calendar-type">
                {t("villageCalendars:labels.calendarType")}
              </Label>
              <Input
                id="vc-calendar-type"
                data-testid="vc-calendar-type"
                type="text"
                value={calendarType}
                onChange={(e) => setCalendarType(e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label for="vc-calendar-color">
                {t("villageCalendars:labels.calendarColor")}
              </Label>
              <Input
                id="vc-calendar-color"
                data-testid="vc-calendar-color"
                type="color"
                value={calendarColor}
                onChange={(e) => setCalendarColor(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <div className="edit-field">
                <Label className="edit-label" for="vc-person-type-ids">
                  {t("villageCalendars:labels.personTypeIds")}
                </Label>
                <div className={classNames("vc-person-type-ids-select")}>
                  <Select
                    inputId="vc-person-type-ids"
                    aria-label="vc-person-type-ids"
                    className="typeahead-selection select-input-box"
                    classNamePrefix="select"
                    isMulti
                    options={personTypeOptions}
                    value={selectedPersonTypeOptions}
                    closeMenuOnSelect={false}
                    onChange={(selected) => {
                      const values = (selected ?? []).map(
                        (option) => option.value
                      );
                      setPersonTypeIds(values);
                    }}
                    placeholder={
                      <div className="select-placeholder-text">
                        {t("villageCalendars:placeholders.personType")}
                      </div>
                    }
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderColor: "#D8E2EE"
                      })
                    }}
                  />
                </div>
              </div>
            </FormGroup>

            <FormGroup check className="mb-4">
              <Input
                id="vc-has-care-companion"
                data-testid="vc-has-care-companion"
                type="checkbox"
                checked={hasCareCompanion}
                onChange={(e) => setHasCareCompanion(e.target.checked)}
              />
              <Label check for="vc-has-care-companion" className="ml-2">
                {t("villageCalendars:labels.hasCareCompanion")}
              </Label>
            </FormGroup>

            {!!villageCalendarsUiStore.errorMessage && (
              <div className="text-danger mb-3" role="alert">
                {villageCalendarsUiStore.errorMessage}
              </div>
            )}

            <div className="village-calendars-create-edit-template__actions">
              <Button
                type="submit"
                color="primary"
                disabled={!canSave || villageCalendarsUiStore.isSaving}
                data-testid="vc-save"
              >
                {villageCalendarsUiStore.isSaving
                  ? t("villageCalendars:buttons.saving")
                  : t("villageCalendars:buttons.save")}
              </Button>
              <Button
                type="button"
                color="secondary"
                outline
                onClick={() => history.push(RoutePaths.VillageCalendars)}
                data-testid="vc-cancel"
              >
                {t("villageCalendars:buttons.cancel")}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default observer(VillageCalendarsCreateEditTemplate);
